import { create } from 'zustand'
import type { CrowdTask, CrowdAssignment, CrowdSubmission } from '../types'
import * as tasksApi from '../api/tasks'
import * as submissionsApi from '../api/submissions'

interface TaskState {
  availableTasks: CrowdTask[]
  myAssignments: CrowdAssignment[]
  currentTask: CrowdTask | null
  currentAssignment: CrowdAssignment | null
  submissions: Record<string, CrowdSubmission> // keyed by task_item_id
  isLoading: boolean
  error: string | null

  fetchAvailableTasks: () => Promise<void>
  fetchMyAssignments: () => Promise<void>
  fetchTaskDetail: (taskId: string) => Promise<void>
  fetchAssignmentDetail: (assignmentId: string) => Promise<void>
  loadSubmissionsForAssignment: (assignmentId: string) => Promise<void>
  claimTask: (taskId: string) => Promise<CrowdAssignment>
  abandonTask: (taskId: string) => Promise<void>
  submitItem: (data: submissionsApi.SubmitItemData) => Promise<CrowdSubmission>
  completeAssignment: (assignmentId: string) => Promise<void>
}

export const useTaskStore = create<TaskState>((set, get) => ({
  availableTasks: [],
  myAssignments: [],
  currentTask: null,
  currentAssignment: null,
  submissions: {},
  isLoading: false,
  error: null,

  fetchAvailableTasks: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await tasksApi.listAvailableTasks()
      set({ availableTasks: data.items, isLoading: false })
    } catch {
      set({ error: 'Failed to load tasks', isLoading: false })
    }
  },

  fetchMyAssignments: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await tasksApi.getMyAssignments()
      set({ myAssignments: data, isLoading: false })
    } catch {
      set({ error: 'Failed to load assignments', isLoading: false })
    }
  },

  fetchTaskDetail: async (taskId: string) => {
    set({ isLoading: true, error: null })
    try {
      const task = await tasksApi.getTaskDetail(taskId)
      set({ currentTask: task, isLoading: false })
    } catch {
      set({ error: 'Failed to load task', isLoading: false })
    }
  },

  fetchAssignmentDetail: async (assignmentId: string) => {
    set({ isLoading: true, error: null })
    try {
      const assignment = await tasksApi.getAssignmentDetail(assignmentId)
      set({ currentAssignment: assignment, isLoading: false })
    } catch {
      set({ error: 'Failed to load assignment', isLoading: false })
    }
  },

  loadSubmissionsForAssignment: async (assignmentId: string) => {
    try {
      const subs = await submissionsApi.getSubmissionsForAssignment(assignmentId)
      const byItemId: Record<string, CrowdSubmission> = {}
      for (const s of subs) byItemId[s.task_item_id] = s
      set((state) => ({ submissions: { ...state.submissions, ...byItemId } }))
    } catch {
      // Non-fatal — submissions just won't show pre-populated
    }
  },

  claimTask: async (taskId: string) => {
    const assignment = await tasksApi.claimTask(taskId)
    set((state) => ({ myAssignments: [...state.myAssignments, assignment] }))
    return assignment
  },

  abandonTask: async (taskId: string) => {
    await tasksApi.abandonTask(taskId)
    set((state) => ({
      myAssignments: state.myAssignments.filter((a) => a.task_id !== taskId),
    }))
  },

  submitItem: async (data: submissionsApi.SubmitItemData) => {
    const submission = await submissionsApi.submitItem(data)
    set((state) => ({
      submissions: { ...state.submissions, [data.task_item_id]: submission },
    }))
    return submission
  },

  completeAssignment: async (assignmentId: string) => {
    await tasksApi.completeAssignment(assignmentId)
    set((state) => ({
      myAssignments: state.myAssignments.map((a) =>
        a.id === assignmentId ? { ...a, status: 'completed' as const } : a
      ),
    }))
  },
}))
